import { normalizePageResponse } from "../lib/pagination";
import { crmDel, crmGet, crmGetBlob, crmPatch, crmPost, crmPut, fsmGet, hrmsGet, imsGet } from "../api/api";
import sessionDB from "../utils/sessionDB";

const BASE = "/leads";

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== "") search.set(key, String(value));
  });
  return search.toString();
}

/** The signed-in employee, recorded as createdBy / updatedBy. */
const empId = () => Number(sessionDB.getEmpId());

const pageOf = (response, paging) => {
  const result = normalizePageResponse(response, paging);
  return { ...result, rows: result.content, totalItems: result.totalElements };
};
/* ---------------------------------------------------------------- leads */

/** GET /saas/crm/leads/search — CRM13S */
export async function fetchLeads(paging) {
  return pageOf(await crmGet(`${BASE}/search?${query(paging)}`, "CRM13S"), paging);
}

/** GET /saas/crm/leads/search/term — CRM13S */
export async function searchLeads(term, paging) {
  return pageOf(await crmGet(`${BASE}/search/term?${query({ term, ...paging })}`, "CRM13S"), paging);
}

/** GET /saas/crm/leads/filter/unassigned — CRM13S */
export async function fetchUnassignedLeads(paging) {
  return pageOf(await crmGet(`${BASE}/filter/unassigned?${query(paging)}`, "CRM13S"), paging);
}

/** GET /saas/crm/leads/filter/status — CRM13S */
export async function fetchLeadsByStatus(status, paging) {
  return pageOf(await crmGet(`${BASE}/filter/status?${query({ status, ...paging })}`, "CRM13S"), paging);
}

/** GET /saas/crm/leads/filter/my — CRM13S */
export async function fetchMyLeads(paging) {
  return pageOf(await crmGet(`${BASE}/filter/my?${query({ empId: empId(), ...paging })}`, "CRM13S"), paging);
}

/** GET /saas/crm/leads/{leadId} — CRM13S */
export async function getLead(leadId) {
  const response = await crmGet(`${BASE}/${leadId}`, "CRM13S");
  return response.data;
}

/** POST /saas/crm/leads/add — CRM13A */
export async function createLead(payload) {
  const response = await crmPost(`${BASE}/add`, { ...payload, createdBy: empId() }, "CRM13A");
  return response.data;
}

/** PUT /saas/crm/leads/update/{leadId} — CRM13U */
export async function updateLead(leadId, payload) {
  const response = await crmPut(`${BASE}/update/${leadId}`, { ...payload, updatedBy: empId() }, "CRM13U");
  return response.data;
}

/** PATCH /saas/crm/leads/update/status/{leadId} — CRM13U */
export async function updateLeadStatus(leadId, change) {
  const response = await crmPatch(`${BASE}/update/status/${leadId}`, { ...change, updatedBy: empId() }, "CRM13U");
  return response.data;
}

/** PATCH /saas/crm/leads/update/assignments/{leadId} — CRM13U */
export async function updateLeadAssignments(leadId, assignments) {
  const response = await crmPatch(
    `${BASE}/update/assignments/${leadId}`,
    { ...assignments, updatedBy: empId() },
    "CRM13U"
  );
  return response.data;
}

/* ----------------------------------------------------------------- lots */

/** GET /saas/crm/leads/{leadId}/lots — CRM13S */
export async function fetchLots(leadId) {
  const response = await crmGet(`${BASE}/${leadId}/lots`, "CRM13S");
  return response.data;
}

/** POST /saas/crm/leads/{leadId}/lots/add — CRM13A */
export async function addLot(leadId, lot) {
  const response = await crmPost(`${BASE}/${leadId}/lots/add`, lot, "CRM13A");
  return response.data;
}

/** PUT /saas/crm/leads/lots/update/{lotId} — CRM13U */
export async function updateLot(lotId, lot) {
  const response = await crmPut(`${BASE}/lots/update/${lotId}`, lot, "CRM13U");
  return response.data;
}

/** DELETE /saas/crm/leads/lots/delete/{lotId} — CRM13D */
export async function deleteLot(lotId) {
  return crmDel(`${BASE}/lots/delete/${lotId}`, "CRM13D");
}

/* ----------------------------------------------------------- case index */

/** The rows of a case-index read; a reply without them carries the reason in its message. */
const caseIndexData = (response) => {
  if (!response.data) throw new Error(response.message);
  return response.data;
};

/** GET /saas/crm/leads/case-index/questions — CRM13S */
export async function fetchCaseIndexQuestions() {
  return caseIndexData(await crmGet(`${BASE}/case-index/questions`, "CRM13S"));
}

/** GET /saas/crm/leads/{leadId}/case-index/reviews — CRM13S. Oldest first; only the newest is open. */
export async function fetchCaseIndexReviews(leadId) {
  return caseIndexData(await crmGet(`${BASE}/${leadId}/case-index/reviews`, "CRM13S"));
}

/** POST /saas/crm/leads/{leadId}/case-index/reviews/save — CRM13A adds, CRM13U edits the latest. */
export async function saveCaseIndexReview(leadId, review) {
  const response = await crmPost(
    `${BASE}/${leadId}/case-index/reviews/save`,
    { ...review, createdBy: empId() },
    review.reviewId ? "CRM13U" : "CRM13A"
  );
  return response.data;
}

/* ----------------------------------------------------------- activities */

/** GET /saas/crm/leads/{leadId}/activities — CRM13S. In the order they were added. */
export async function fetchActivities(leadId) {
  const response = await crmGet(`${BASE}/${leadId}/activities`, "CRM13S");
  return response.data;
}

/** GET /saas/crm/leads/{leadId}/activities/filter/my — CRM13S. "My Activities". */
export async function fetchMyActivities(leadId) {
  const response = await crmGet(`${BASE}/${leadId}/activities/filter/my?${query({ empId: empId() })}`, "CRM13S");
  return response.data;
}

/** POST /saas/crm/leads/{leadId}/activities/add — CRM13A */
export async function addActivity(leadId, activity) {
  const response = await crmPost(`${BASE}/${leadId}/activities/add`, { ...activity, createdBy: empId() }, "CRM13A");
  return response.data;
}

/** PUT /saas/crm/leads/activities/update/{activityId} — CRM13U. Null fields are left as they are. */
export async function updateActivity(activityId, change) {
  const response = await crmPut(`${BASE}/activities/update/${activityId}`, { ...change, updatedBy: empId() }, "CRM13U");
  return response.data;
}

/** DELETE /saas/crm/leads/activities/delete/{activityId} — CRM13D. Only its creator may delete it. */
export async function deleteActivity(activityId) {
  return crmDel(`${BASE}/activities/delete/${activityId}?${query({ empId: empId() })}`, "CRM13D");
}

/* ------------------------------------------------------------ messages */

/** GET /saas/crm/leads/{leadId}/messages — CRM13S. Newest page first. */
export async function fetchMessages(leadId, page = 0, size = 20, sortDir = "DESC") {
  const response = await crmGet(`${BASE}/${leadId}/messages?${query({ page, size, sortDir })}`, "CRM13S");
  const data = response?.data || {};
  if (response?.lastReadMessageId !== undefined && data.lastReadMessageId === undefined) {
    data.lastReadMessageId = response.lastReadMessageId;
  }
  if (response?.lastMessageReadId !== undefined && data.lastMessageReadId === undefined) {
    data.lastMessageReadId = response.lastMessageReadId;
  }
  if (response?.unreadCount !== undefined && data.unreadCount === undefined) {
    data.unreadCount = response.unreadCount;
  }
  return data; // PagedResponse<CrmLeadMessageDTO>
}

/** POST /saas/crm/leads/{leadId}/messages/add — CRM13A */
export async function addMessage(leadId, messageText, attachments = [], mentionData = {}) {
  const response = await crmPost(
    `${BASE}/${leadId}/messages/add`,
    { messageText, attachments, createdBy: empId(), ...mentionData },
    "CRM13A"
  );
  return response.data;
}

/** GET /saas/crm/leads/{leadId}/messages/{messageId}/replies — CRM13S */
export async function fetchReplies(leadId, messageId) {
  const response = await crmGet(`${BASE}/${leadId}/messages/${messageId}/replies`, "CRM13S");
  return response.data;
}

/** POST /saas/crm/leads/{leadId}/messages/{messageId}/replies/add — CRM13A */
export async function addReply(leadId, messageId, replyText, attachments = [], mentionData = {}) {
  const response = await crmPost(
    `${BASE}/${leadId}/messages/${messageId}/replies/add`,
    { replyText, attachments, createdBy: empId(), ...mentionData },
    "CRM13A"
  );
  return response.data;
}

/** GET message/reply attachment as a Blob so it is saved rather than opened. */
export const downloadMessageAttachment = (leadId, messageId, index) =>
  crmGetBlob(`${BASE}/${leadId}/messages/${messageId}/attachments/${index}`, "CRM13S");

export const downloadReplyAttachment = (leadId, replyId, index) =>
  crmGetBlob(`${BASE}/${leadId}/messages/replies/${replyId}/attachments/${index}`, "CRM13S");

/** POST /saas/crm/leads/{leadId}/messages/read — CRM13S. Advances read pointer. */
export async function markMessagesRead(leadId, upToMessageId) {
  const params = { empId: empId(), ...(upToMessageId != null ? { upToMessageId } : {}) };
  const response = await crmPost(`${BASE}/${leadId}/messages/read?${query(params)}`, {}, "CRM13S");
  return response.data; // CrmLeadMessageUnreadDTO
}

/** GET /saas/crm/leads/{leadId}/messages/unread-count — CRM13S */
export async function getUnreadCount(leadId) {
  const response = await crmGet(`${BASE}/${leadId}/messages/unread-count?${query({ empId: empId() })}`, "CRM13S");
  return response.data; // CrmLeadMessageUnreadDTO
}

/* -------------------------------------------------------------- lookups */

const contactName = (row) => `${row.firstName} ${row.lastName ?? ""}`.trim();

/** GET /saas/crm/customer-info/search — CRM01S */
export async function fetchCustomers() {
  const response = await crmGet(
    `/customer-info/search?${query({ page: 0, size: 200, sortBy: "companyName", sortDir: "ASC" })}`,
    "CRM01S"
  );
  return response.data.content.map((row) => ({ id: row.customerId, name: row.companyName }));
}

/** GET /saas/crm/customer-info/{customerId} — CRM01S */
export async function getCustomer(customerId) {
  const response = await crmGet(`/customer-info/${customerId}`, "CRM01S");
  return { id: response.data.customerId, name: response.data.companyName };
}

/** GET /saas/crm/customer-contact/search/customer — CRM01S */
export async function fetchContacts(customerId) {
  const response = await crmGet(`/customer-contact/search/customer?${query({ customerId, page: 0, size: 200 })}`, "CRM01S");
  return response.data.content.map((row) => ({ id: row.contactId, name: contactName(row) }));
}

/** GET /saas/crm/customer-contact/{contactId} — CRM01S */
export async function getContact(contactId) {
  const response = await crmGet(`/customer-contact/${contactId}`, "CRM01S");
  const row = response.data;
  return { id: row.contactId, name: contactName(row), email: row.email, phone: row.phone };
}

/** GET /saas/hrms/org/industry_types — HR20A. The lead verticals. */
export async function fetchIndustryTypes() {
  const rows = await hrmsGet(`/org/industry_types?${query({ tenantId: sessionDB.getTenantId() })}`, "HR20A");
  return rows.map((row) => row.industryType);
}

/** GET /saas/hrms/current-employment/search/canonicalRoleName — HR02S */
export async function fetchEmployeesByRole(searchTerm) {
  const response = await hrmsGet(
    `/current-employment/search/canonicalRoleName?${query({ searchTerm, page: 0, size: 100 })}`,
    "HR02S"
  );
  return response.data.content.map((row) => ({ id: row.empId, name: row.name }));
}

/** GET /saas/hrms/emp-info/search/{empId} — HR01S. Names a saved assignee. */
export async function getEmployee(empId) {
  const response = await hrmsGet(`/emp-info/search/${empId}`, "HR01S");
  return { id: response.data.empId, name: response.data.fullName };
}

/** GET /saas/fsm/products-services/search — FSM01S. The Systems Required options. */
export async function fetchSystemCategories() {
  const response = await fsmGet(`/products-services/search?${query({ page: 0, size: 200 })}`, "FSM01S");
  return response.data.content.map((row) => row.productServiceName);
}

/** GET /saas/ims/product-metrics/search/category?category=Currency — IMS04S */
export async function fetchCurrencyOptions() {
  const response = await imsGet(
    `/product-metrics/search/category?${query({ category: "Currency", page: 0, size: 30 })}`,
    "IMS04S"
  );
  return response.data.content.map((row) => ({ id: row.metricId, label: row.abbreviation }));
}

export const leadManagementService = {
  fetchLeads,
  searchLeads,
  fetchUnassignedLeads,
  fetchLeadsByStatus,
  fetchMyLeads,
  getLead,
  createLead,
  updateLead,
  updateLeadStatus,
  updateLeadAssignments,
  fetchLots,
  addLot,
  updateLot,
  deleteLot,
  fetchCaseIndexQuestions,
  fetchCaseIndexReviews,
  saveCaseIndexReview,
  fetchActivities,
  fetchMyActivities,
  addActivity,
  updateActivity,
  deleteActivity,
  fetchMessages,
  addMessage,
  fetchReplies,
  addReply,
  downloadMessageAttachment,
  downloadReplyAttachment,
  markMessagesRead,
  getUnreadCount,
  fetchCustomers,
  getCustomer,
  fetchContacts,
  getContact,
  fetchIndustryTypes,
  fetchEmployeesByRole,
  getEmployee,
  fetchSystemCategories,
  fetchCurrencyOptions,
};

export default leadManagementService;
